import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from './registerSW';
import { useToast } from '@/components/toast/ToastProvider';

// How often a long-lived / installed PWA polls for a freshly deployed service
// worker. Paired with a visibilitychange check so returning to the tab triggers
// an immediate look — that covers the common "left it open, came back" case
// without waiting out the full interval.
export const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Proactive "new version available" flow (issue #265).
 *
 * With `registerType: 'prompt'`, a freshly deployed service worker installs but
 * waits rather than swapping under the open tab. This component:
 *  - surfaces a non-blocking, persistent toast with a "Reload" action when an
 *    update is waiting (`needRefresh`); the action calls `updateServiceWorker(true)`
 *    to skipWaiting + reload cleanly — no stale-chunk 404, no ErrorBoundary flash;
 *  - shows a one-time "Ready to work offline" toast on first install;
 *  - periodically (and whenever the tab regains focus) asks the registration to
 *    check for a new SW, so installed PWAs notice deploys without a manual refresh.
 *
 * Dismissing / ignoring the toast leaves the #264 reactive stale-chunk fallback
 * intact as the safety net. Renders nothing.
 */
export function PwaUpdatePrompt() {
  const { t } = useTranslation();
  const { show } = useToast();
  const [registration, setRegistration] = useState<ServiceWorkerRegistration>();

  const {
    needRefresh: [needRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, r) {
      if (r) setRegistration(r);
    },
  });

  // A new version is waiting → offer a clean, user-initiated reload.
  useEffect(() => {
    if (!needRefresh) return;
    show(t('pwa.newVersion'), {
      durationMs: 0,
      dismissible: true,
      action: {
        label: t('pwa.reload'),
        onClick: () => {
          void updateServiceWorker(true);
        },
      },
    });
  }, [needRefresh, show, t, updateServiceWorker]);

  // First install → one-time confirmation that the app works offline.
  useEffect(() => {
    if (!offlineReady) return;
    show(t('pwa.offlineReady'));
    setOfflineReady(false);
  }, [offlineReady, setOfflineReady, show, t]);

  // Poll for new deploys on an interval and whenever the tab regains focus, so
  // installed PWAs notice a deploy without waiting for the next navigation.
  useEffect(() => {
    if (!registration) return;
    const check = () => {
      // Network hiccups are non-fatal; the next tick / focus retries.
      void registration.update().catch(() => {});
    };
    const interval = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [registration]);

  return null;
}
